import { useEffect } from "react";

const EFFECTIVE_DATE = "February 14, 2026";
const CONTACT_EMAIL = "rene@thewomenseconomy.org";

export default function Privacy() {
  useEffect(() => {
    try {
      window.gtag?.("event", "privacy_view", { surface: "privacy" });
    } catch { /* noop */ }
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      <main className="mx-auto max-w-[720px] px-5 sm:px-8 py-16 sm:py-24 space-y-16 sm:space-y-20">
        {/* Header */}
        <header className="space-y-6" data-testid="privacy-header">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-orange">
            Privacy · Effective {EFFECTIVE_DATE}
          </span>
          <h1
            data-testid="privacy-title"
            className="font-display font-bold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            How SheScan handles your data.
          </h1>
          <div className="space-y-4 text-lg sm:text-xl text-ink-soft leading-relaxed">
            <p className="text-ink">
              Plain-English summary, and the whole of what matters most:
            </p>
            <ul className="space-y-2 border border-line bg-white p-5 sm:p-6">
              <Bullet>
                We do <strong className="text-ink">not</strong> run
                advertising pixels. No Meta Pixel. No Pinterest Tag. No
                third-party ad-network beacons of any kind.
              </Bullet>
              <Bullet>
                We do <strong className="text-ink">not</strong> share your
                data with ad networks or Google&apos;s advertising graph.
                Google Signals is turned off inside our GA4 property.
              </Bullet>
              <Bullet>
                We do <strong className="text-ink">not</strong> sell your
                data. Full stop. There is no future plan to sell your data.
                Brands who partner with us can see analytics about their own
                brand only. We do not sell or license data about our users
                to anyone, in any form.
              </Bullet>
              <Bullet>
                Every brand verification is done by a human. Ownership
                documentation you submit is read by our team, not scanned by
                an ad-tech vendor.
              </Bullet>
            </ul>
            <p>
              The rest of this page describes what data we actually collect,
              why, who processes it, how long we keep it, and how you can ask
              us to delete it.
            </p>
          </div>
        </header>

        {/* 01 — Who we are */}
        <Section num="01" title="Who this policy applies to">
          <p>
            SheScan is a barcode-verification service run by The Women&apos;s
            Economy. This policy covers the marketing website{" "}
            <a href="https://shescan.app" className="link-under text-ink">
              shescan.app
            </a>
            , the brand kit at{" "}
            <a href="https://shescan.app/kit" className="link-under text-ink">
              shescan.app/kit
            </a>
            , the investor gate at{" "}
            <a
              href="https://shescan.app/investors"
              className="link-under text-ink"
            >
              shescan.app/investors
            </a>
            , the admin dashboard at{" "}
            <a href="https://shescan.app/admin" className="link-under text-ink">
              shescan.app/admin
            </a>
            , and the SheScan mobile web app at{" "}
            <a href="https://app.shescan.app" className="link-under text-ink">
              app.shescan.app
            </a>
            . It also covers the SheScan Chrome extension.
          </p>
        </Section>

        {/* 02 — What we collect */}
        <Section num="02" title="What we collect, and why">
          <SubHead>From consumers who use the app or extension</SubHead>
          <p>
            When you scan a barcode in the mobile web app or the Chrome
            extension, we receive:
          </p>
          <List>
            <li>The barcode (UPC) you scanned, so we can look it up.</li>
            <li>
              Your IP address and browser user-agent, standard in every web
              request; we use these for basic abuse-prevention and
              troubleshooting.
            </li>
            <li>
              The verification result we returned to you, so we can improve
              coverage over time.
            </li>
          </List>
          <p>
            We do not link scans to a personal identity. We do not build a
            purchase profile about you. We do not sell scan data.
          </p>

          <SubHead>From brands who submit through the intake form</SubHead>
          <p>
            When a brand submits the intake form at{" "}
            <span className="font-mono text-sm">shescan.app/#signup</span>, we
            collect the brand or company name, contact email, website URL,
            and category, plus any UTM attribution parameters if you arrived
            from a tagged link. This information is used to verify ownership
            and, if verified, to list your brand in the SheScan database.
          </p>

          <SubHead>From everyone who visits any page</SubHead>
          <p>
            We use Google Analytics 4 to count pageviews and measure how
            people use the site. GA4 sets a first-party cookie{" "}
            <span className="font-mono text-sm">_ga</span> on your browser
            and records: page URL, referrer, approximate location derived
            from IP, device type, and events we explicitly define (for
            example{" "}
            <span className="font-mono text-sm">generate_lead</span> when a
            brand submits the intake form,{" "}
            <span className="font-mono text-sm">kit_download_badge</span> when
            a verified brand downloads their kit).
          </p>
          <p>
            <strong className="text-ink">
              Google Signals is turned off in our GA4 property.
            </strong>{" "}
            That means GA4 is not combining your visit with Google&apos;s
            cross-device advertising graph, and we are not enabling
            remarketing audiences.
          </p>
        </Section>

        {/* 03 — Vendors */}
        <Section num="03" title="Who processes this data on our behalf">
          <p>Named vendors, no others:</p>
          <List>
            <li>
              <strong className="text-ink">MongoDB</strong> — our primary
              database. Brand submissions, scan-log summaries, and the
              extension waitlist live here. Hosted in the United States on
              infrastructure managed by Emergent, our hosting provider.
            </li>
            <li>
              <strong className="text-ink">Airtable</strong> — we mirror
              brand submissions and extension waitlist entries into an
              Airtable base so our verification team can read and act on
              them.
            </li>
            <li>
              <strong className="text-ink">Resend</strong> — sends
              transactional emails (welcome, verification decisions,
              follow-ups). Your email address is transmitted to Resend so
              they can deliver the message. We do not run marketing sends
              through Resend.
            </li>
            <li>
              <strong className="text-ink">Google Analytics 4</strong> —
              first-party site analytics only. Google Signals off. No
              ads-personalization sharing enabled.
            </li>
            <li>
              <strong className="text-ink">Emergent</strong> — hosting
              platform. Serves the site and stores our data at rest.
            </li>
          </List>
          <p>
            We do not use Meta Pixel, Pinterest Tag, TikTok Pixel, LinkedIn
            Insight Tag, or any other advertising-network tracker. We do not
            share data with data brokers.
          </p>
        </Section>

        {/* 04 — Retention */}
        <Section num="04" title="How long we keep it">
          <List>
            <li>
              <strong className="text-ink">Brand submissions</strong> —
              kept indefinitely while your brand is listed. You can request
              deletion at any time (see below).
            </li>
            <li>
              <strong className="text-ink">Extension waitlist entries</strong>{" "}
              — kept until we have contacted you at least once, then either
              converted into an active brand record or deleted on request.
            </li>
            <li>
              <strong className="text-ink">Scan records</strong> — we keep
              scan records only as long as needed to operate the app, and
              delete them when they are no longer needed. Aggregated
              statistics (for example, &ldquo;how many people looked for a
              women-owned coffee brand this week&rdquo;) may be kept longer
              and cannot be linked back to a person.
            </li>
            <li>
              <strong className="text-ink">GA4 event data</strong> — retained
              at GA4&apos;s default of 14 months, then automatically deleted
              by Google.
            </li>
            <li>
              <strong className="text-ink">Server access logs</strong> —
              rotated inside 30 days.
            </li>
          </List>
        </Section>

        {/* 05 — Rights */}
        <Section num="05" title="Your rights, and how to use them">
          <p>You can ask us to:</p>
          <List>
            <li>Tell you what we hold about you.</li>
            <li>Correct anything that is wrong.</li>
            <li>Delete your data entirely.</li>
            <li>
              Export what we hold in a machine-readable format (JSON or
              CSV).
            </li>
            <li>
              Stop sending you email — reply &ldquo;unsubscribe&rdquo; to any
              message from us, or email us directly.
            </li>
          </List>
          <p>
            To make any of these requests, email us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="link-under text-ink"
            >
              {CONTACT_EMAIL}
            </a>
            . We aim to respond within 14 days.
          </p>
          <p>
            If you are located in the European Union, the United Kingdom,
            California, or another jurisdiction with specific data-protection
            laws, you have additional rights under those laws (GDPR, UK GDPR,
            CCPA/CPRA). The requests above are how you exercise them here.
            You also have the right to complain to a supervisory authority.
          </p>
        </Section>

        {/* 06 — Legal basis */}
        <Section num="06" title="Legal basis (for GDPR readers)">
          <List>
            <li>
              Consumer scans and analytics —{" "}
              <em>legitimate interest</em>: running a service that requires
              a functioning site and a way to improve it.
            </li>
            <li>
              Brand intake and communications — <em>contract</em> and{" "}
              <em>legitimate interest</em>: we cannot verify or list you
              without your submission.
            </li>
            <li>
              Transactional email — the direct{" "}
              <em>consequence of the form you submitted</em>. We do not
              enroll you in marketing lists.
            </li>
          </List>
        </Section>

        {/* 07 — Cookies */}
        <Section num="07" title="Cookies and similar storage">
          <p>We set two things in your browser:</p>
          <List>
            <li>
              <span className="font-mono text-sm">_ga</span> — a
              first-party cookie for Google Analytics, containing an
              anonymous visitor ID.
            </li>
            <li>
              <span className="font-mono text-sm">shescan.attribution</span>{" "}
              — session storage (cleared when you close the tab) that
              remembers which link brought you to the site so we can credit
              partners correctly.
            </li>
          </List>
          <p>
            You can block cookies in your browser settings. Blocking them
            does not break the site.
          </p>
        </Section>

        {/* 08 — Changes */}
        <Section num="08" title="Changes to this policy">
          <p>
            If we materially change how we collect or process data, we will
            update this page and change the effective date at the top. If
            you have submitted an active brand record, we will also email
            you at the address on file.
          </p>
        </Section>

        {/* Contact / footer */}
        <footer
          data-testid="privacy-footer"
          className="pt-10 border-t border-line font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft space-y-2"
        >
          <div>
            Questions or requests?{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-ink hover:text-brand-orange transition-colors underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
          <div>Effective {EFFECTIVE_DATE}</div>
        </footer>
      </main>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <section
      data-testid={`privacy-section-${num}`}
      className="space-y-5 sm:space-y-6"
    >
      <div className="flex items-baseline gap-4 border-b border-line pb-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
          {num}
        </span>
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink leading-[1.15]">
          {title}
        </h2>
      </div>
      <div className="space-y-5 text-base sm:text-lg text-ink-soft leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function SubHead({ children }) {
  return (
    <h3 className="font-sans font-bold text-sm uppercase tracking-[0.22em] text-brand-orange pt-2">
      {children}
    </h3>
  );
}

function List({ children }) {
  return (
    <ul className="space-y-3 list-disc pl-6 marker:text-brand-orange">
      {children}
    </ul>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-ink-soft">
      <span
        className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0"
        aria-hidden
      />
      <span>{children}</span>
    </li>
  );
}
